import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useSelector, useDispatch } from "react-redux";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Chip, Grid, Button, Card, CardContent, Stack, TextField, Typography } from "@mui/material";
import {
  deleteProperties,
  updateProperty,
} from "../services/propertyService";
import { markerOptionsWithLabels } from "../markerColors";
import { loadPropertyIntoAssessor } from "../services/assessorLoad";
import {
  resetForm,
  setAssessorValue,
  setBuildingAddr,
  setCIDValue,
  setContacts,
  setData,
  setOwnerMailing,
  setOwnerName,
  setParcelID,
  setPropertyDesc,
  setExempt,
  setMarker,
  setImportModal,
  setCustomFields,
} from "../features/assessorSlice";
import { countActiveFilters } from "./filterPanel";
import { canWriteCid } from "../utils/permissions";
import { getCIDProperties } from "../services/cidService";
import { Box } from "@mui/system";
import { resetContacts } from "../features/contactSlice";
import { ImportExport } from "@mui/icons-material";
import { persistSelectedProperty } from "../utils/assessorPersistence";

// Serializes a Date from the grid's date editor back to the "YYYY-MM-DD"
// string shape custom fields are stored in.
const toDateString = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${value.getFullYear()}-${month}-${day}`;
};

// valueSetter for cf_* columns: writes the edited value back into the row's
// custom_fields object (deleting empty values, matching setCustomFieldValue).
const customFieldSetter = (name, transform = (v) => v) => (params) => {
  const next = { ...(params.row.custom_fields || {}) };
  const value = transform(params.value);
  if (
    value === "" ||
    value === null ||
    value === undefined ||
    (typeof value === "number" && Number.isNaN(value))
  ) {
    delete next[name];
  } else {
    next[name] = value;
  }
  return { ...params.row, custom_fields: next };
};

const PropertyTable = (props) => {
  const [selected, setSelected] = useState([]);
  const [tableSearch, setTableSearch] = useState("");
  const gridWrapRef = useRef(null);
  const [gridHeight, setGridHeight] = useState(560);
  const cid = useSelector((state) => state.assessor.value.form.cid);
  const data = useSelector((state) => state.assessor.value.data);
  const filters = useSelector((state) => state.assessor.value.filters);
  const mils = useSelector((state) => state.assessor.value.mils);
  const legend = useSelector((state) => state.cid.value.legend);
  const auth = useSelector((state) => state.auth.value);
  const cidWritable = canWriteCid(auth, cid);
  const editorTransferID = useSelector(
    (state) => state.assessor.value.form.transferID
  );
  const propertyFieldDefs = useSelector((state) => state.settings.value.propertyFields);
  const contactFieldDefs = useSelector((state) => state.settings.value.contactFields);
  const propertyFields = useMemo(
    () => propertyFieldDefs.filter((f) => !f.archived),
    [propertyFieldDefs]
  );
  const contactFields = useMemo(
    () => contactFieldDefs.filter((f) => !f.archived),
    [contactFieldDefs]
  );
  const activeFieldNames = useMemo(
    () => new Set(propertyFields.map((f) => f.internal_name)),
    [propertyFields]
  );
  const dispatch = useDispatch();

  // Reflect the property currently loaded in the editor as the (single) grid
  // selection — this keeps the checkbox in sync with row clicks, refresh
  // rehydration, and "Open in Quick Assessor" alike.
  useEffect(() => {
    setSelected(
      editorTransferID && editorTransferID !== -1 ? [editorTransferID] : []
    );
  }, [editorTransferID]);

  // Stretch the grid so its bottom edge always meets the bottom of the window,
  // whatever the viewport height. Measured (not a magic constant) so it stays
  // correct as the header above it changes.
  const recomputeHeight = useCallback(() => {
    const el = gridWrapRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    // setState is a no-op when the value is unchanged, so re-measuring on every
    // render (below) is cheap and won't loop.
    setGridHeight(Math.max(360, window.innerHeight - top - 24));
  }, []);

  // Re-measure after every commit (data load, header wrap, editor toggle) and
  // whenever the window resizes.
  useLayoutEffect(() => {
    recomputeHeight();
  });
  useEffect(() => {
    window.addEventListener("resize", recomputeHeight);
    return () => window.removeEventListener("resize", recomputeHeight);
  }, [recomputeHeight]);

  const selectedId = selected.length ? selected[selected.length - 1] : null;

  const activeFilterCount = countActiveFilters(filters, activeFieldNames);

  const filteredRows = useMemo(() => {
    const search = tableSearch.trim().toLowerCase();

    return data.filter((row) => {
      if (search) {
        const fields = [
          row.parcelID,
          row.owner_name,
          row.owner_mailing,
          row.building_address,
          row.property_desc,
          row.name,
          row.company,
          row.phone_number,
          ...Object.values(row.custom_fields || {}),
          ...Object.values(row.contact_custom_fields || {}),
        ];
        const hit = fields.some((value) =>
          String(value ?? "").toLowerCase().includes(search)
        );
        if (!hit) {
          return false;
        }
      }

      if (filters.markers.length) {
        const marker = row.flag_color || "none";
        if (!filters.markers.includes(marker)) {
          return false;
        }
      }

      for (const [name, filter] of Object.entries(filters.custom)) {
        if (!activeFieldNames.has(name)) {
          continue; // stale filter on an archived/removed field — ignore
        }
        const value = row.custom_fields?.[name];
        if (filter.q !== undefined) {
          if (!String(value ?? "").toLowerCase().includes(filter.q.toLowerCase())) {
            return false;
          }
        }
        if (filter.min !== undefined || filter.max !== undefined) {
          const num = typeof value === "number" ? value : parseFloat(value);
          if (!Number.isFinite(num)) return false;
          if (filter.min !== undefined && num < filter.min) return false;
          if (filter.max !== undefined && num > filter.max) return false;
        }
        if (filter.from !== undefined || filter.to !== undefined) {
          const date = String(value || "");
          if (!date) return false;
          if (filter.from !== undefined && date < filter.from) return false;
          if (filter.to !== undefined && date > filter.to) return false;
        }
        if (filter.selected !== undefined) {
          if (!filter.selected.includes(String(value ?? ""))) return false;
        }
      }

      return true;
    });
  }, [data, tableSearch, filters, activeFieldNames]);

  const columns = useMemo(() => [
    {
      field: "flag_color",
      width: 80,
      headerName: "Mark",
      editable: true,
      type: "singleSelect",
      valueOptions: [
        { value: "", label: "No marker" },
        ...markerOptionsWithLabels(legend).map(({ value, label }) => ({
          value,
          label,
        })),
      ],
      valueGetter: ({ value }) => value || "",
      valueSetter: (params) => ({
        ...params.row,
        flag_color: params.value || null,
      }),
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor: params.value ? params.value : "gray",
            minWidth: "100%",
            borderRadius: 10,
          }}
        >
          &nbsp;
        </Box>
      ),
    },
    {
      field: "last_updated",
      headerName: "Last Updated",
      type: "date",
      width: 100,
      valueGetter: ({ value }) => value && new Date(value),
    },
    { field: "parcelID", headerName: "Parcel ID", width: 150, editable: true },
    { field: "owner_name", headerName: "Owner Name", width: 150, editable: true },
    { field: "owner_mailing", headerName: "Mailing Address", width: 150, editable: true },
    { field: "building_address", headerName: "Building Address", width: 150, editable: true },
    {
      field: "assessor_value",
      headerName: "Assessor Value",
      width: 150,
      type: "number",
      editable: true,
      valueGetter: ({ value }) =>
        value === null || value === undefined || value === ""
          ? null
          : Number(value),
    },
    {
      field: "cid_value",
      headerName: "CID Value",
      width: 100,
      type: "number",
      editable: true,
      valueGetter: ({ value }) =>
        value === null || value === undefined || value === ""
          ? null
          : Number(value),
    },
    { field: "property_desc", headerName: "Property Desc", width: 150, editable: true },

    { field: "name", headerName: "Name", width: 150 },
    { field: "phone_number", headerName: "Phone Number", width: 150 },
    { field: "email", headerName: "Email", width: 150 },
    { field: "company", headerName: "Company", width: 150 },
    { field: "secondary_contact", headerName: "Secondary Contact", width: 150 },
    { field: "secondary_phone", headerName: "Secondary Phone", width: 150 },
    { field: "secondary_email", headerName: "Secondary Email", width: 150 },
    { field: "notes", headerName: "Notes", width: 150 },
    { field: "callsMade", headerName: "Calls Made", width: 150, valueGetter: ({ value }) => value ? value : 0 },
    { field: "followUpCalls", headerName: "Follow Up Calls", width: 150, valueGetter: ({ value }) => value ? value : 0 },
    { field: "emailsSent", headerName: "Emails Sent", width: 150, valueGetter: ({ value }) => value ? value : 0 },
    { field: "dropIns", headerName: "Drops In", width: 150, valueGetter: ({ value }) => value ? value : 0 },

    ...propertyFields.map((f) => {
      const base = {
        field: `cf_${f.internal_name}`,
        headerName: f.label,
        width: 150,
        editable: true,
      };
      switch (f.field_type) {
        case "number":
          return {
            ...base,
            type: "number",
            valueGetter: (params) => {
              const value = params.row.custom_fields?.[f.internal_name];
              return value === undefined || value === "" ? null : Number(value);
            },
            valueSetter: customFieldSetter(f.internal_name),
          };
        case "date":
          return {
            ...base,
            type: "date",
            valueGetter: (params) => {
              const value = params.row.custom_fields?.[f.internal_name];
              return value ? new Date(`${value}T00:00:00`) : null;
            },
            valueSetter: customFieldSetter(f.internal_name, toDateString),
          };
        case "select":
          return {
            ...base,
            type: "singleSelect",
            valueOptions: [
              { value: "", label: "—" },
              ...(f.options || []).map((option) => ({ value: option, label: option })),
            ],
            valueGetter: (params) =>
              params.row.custom_fields?.[f.internal_name] ?? "",
            valueSetter: customFieldSetter(f.internal_name),
          };
        default:
          return {
            ...base,
            valueGetter: (params) =>
              params.row.custom_fields?.[f.internal_name] ?? "",
            valueSetter: customFieldSetter(f.internal_name),
          };
      }
    }),
    ...contactFields.map((f) => ({
      field: `ccf_${f.internal_name}`,
      headerName: `${f.label} (Contact)`,
      width: 150,
      valueGetter: (params) => params.row.contact_custom_fields?.[f.internal_name] ?? "",
    })),
  ], [propertyFields, contactFields, legend]);

  const processRowUpdate = useCallback(
    async (newRow, oldRow) => {
      // Preserve blank/null monetary values — coercing them to 0 on an
      // unrelated cell edit would silently overwrite imported rows (and a
      // cid_value of 0 reads as "exempt").
      const toNumberOrNull = (value) => {
        if (value === null || value === undefined || value === "") {
          return null;
        }
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
      };
      const assessorValue = toNumberOrNull(newRow.assessor_value);
      let cidValue = toNumberOrNull(newRow.cid_value);
      const oldAssessor = toNumberOrNull(oldRow.assessor_value);
      const oldCid = toNumberOrNull(oldRow.cid_value);

      // Mirror the editor's mils math: editing the assessor value recomputes
      // the CID value, unless the row is exempt (cid_value 0) or the user
      // edited the CID value directly.
      const assessorChanged = assessorValue !== oldAssessor;
      const cidChanged = cidValue !== oldCid;

      // The grid calls processRowUpdate on every committed cell edit, even when
      // the value is unchanged (e.g. entering and leaving an editor). Skip the
      // save — and the "Updated Property!" snackbar — when nothing actually
      // changed across the editable columns.
      const textChanged =
        newRow.parcelID !== oldRow.parcelID ||
        newRow.owner_name !== oldRow.owner_name ||
        newRow.owner_mailing !== oldRow.owner_mailing ||
        newRow.building_address !== oldRow.building_address ||
        newRow.property_desc !== oldRow.property_desc ||
        (newRow.flag_color || null) !== (oldRow.flag_color || null);
      const customFieldsChanged =
        JSON.stringify(newRow.custom_fields || {}) !==
        JSON.stringify(oldRow.custom_fields || {});
      if (!textChanged && !assessorChanged && !cidChanged && !customFieldsChanged) {
        return oldRow;
      }

      if (
        assessorChanged &&
        !cidChanged &&
        assessorValue !== null &&
        oldCid !== 0
      ) {
        cidValue = assessorValue * (parseFloat(mils) || 0);
      }

      const payload = {
        transferID: newRow.id,
        parcelID: newRow.parcelID,
        ownerName: newRow.owner_name,
        ownerMailing: newRow.owner_mailing,
        buildingAddress: newRow.building_address,
        assessorValue,
        cidValue,
        propertyDesc: newRow.property_desc,
        flagColor: newRow.flag_color || null,
        customFields: newRow.custom_fields || {},
        // Pass the row's contacts through so the save doesn't clear them.
        contact_ids: Array.isArray(newRow.contact_ids)
          ? newRow.contact_ids
          : newRow.contact_id
            ? [newRow.contact_id]
            : [],
      };

      // Throws on failure — the grid reverts the cell.
      const updated = await updateProperty(payload);
      const merged = { ...newRow, ...updated };
      dispatch(setData(data.map((row) => (row.id === merged.id ? merged : row))));

      // Keep the side editor in sync when it has this property open.
      if (editorTransferID === merged.id) {
        dispatch(setParcelID(merged.parcelID));
        dispatch(setOwnerName(merged.owner_name));
        dispatch(setOwnerMailing(merged.owner_mailing));
        dispatch(setMarker(merged.flag_color));
        dispatch(setCustomFields(merged.custom_fields || {}));
        dispatch(setBuildingAddr(merged.building_address));
        dispatch(setPropertyDesc(merged.property_desc));
        dispatch(setExempt(parseFloat(merged.cid_value) === 0));
        dispatch(setAssessorValue(merged.assessor_value));
        dispatch(setCIDValue(merged.cid_value));
      }

      return merged;
    },
    [data, dispatch, editorTransferID, mils]
  );

  const handleProcessRowUpdateError = useCallback((err) => {
    // updateProperty already surfaced a snackbar; just log for debugging.
    console.log(err);
  }, []);

  const handleSelect = async (model) => {
    props.toggleLookup(true);
    // Enforce single selection: keep only the most recently clicked row so a
    // second checkbox click replaces (rather than adds to) the selection.
    const next = model.length ? [model[model.length - 1]] : [];
    setSelected(next);
    if (next.length) {
      await loadPropertyIntoAssessor(dispatch, next[0]);
    } else {
      persistSelectedProperty(null);
      dispatch(resetForm());
      dispatch(resetContacts());
      dispatch(setContacts([]));
    }
  };

  return (
    <Grid container mb={2} spacing={1}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">
                  Property Results ({filteredRows.length}/{data.length})
                </Typography>
                {activeFilterCount > 0 ? (
                  <Chip
                    size="small"
                    color="primary"
                    label={`${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} on`}
                  />
                ) : null}
              </Stack>
              <TextField
                size="small"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                label="Filter rows"
                placeholder="Parcel, owner, address, contact..."
                sx={{ minWidth: { xs: "100%", md: 320 } }}
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} ref={gridWrapRef} sx={{ height: gridHeight }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSize={20}
          rowsPerPageOptions={[20]}
          checkboxSelection
          disableMultipleSelection
          selectionModel={selected}
          onSelectionModelChange={(p) => handleSelect(p)}
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
          isCellEditable={() => cidWritable}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          sx={{
            backgroundColor: "white",
            borderRadius: 1,
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "last_updated", sort: "desc" }],
            },
          }}
          components={{
            Toolbar: () => {
              return cid ? (
                <GridToolbarContainer>
                  <Button
                    color="success"
                    startIcon={<ImportExport />}
                    disabled={!cidWritable}
                    onClick={() => dispatch(setImportModal(true))}
                  >
                    Import
                  </Button>
                  <GridToolbarExport />
                  <Button
                    color="error"
                    disabled={!cidWritable || selectedId === null}
                    onClick={async () => {
                      deleteProperties(selected).then(async () => {
                        const refreshProperties = await getCIDProperties(cid);
                        dispatch(setData(refreshProperties.data));
                        // Drop the deleted rows from selection and the editor so
                        // nothing dangles.
                        setSelected([]);
                        persistSelectedProperty(null);
                      });
                    }}
                  >
                    Delete Row
                  </Button>
                </GridToolbarContainer>
              ) : null;
            },
          }}
        />
      </Grid>

      {/* <Grid item> MOVE TO LOOKUP TOOL
        <Button sx={{ m: 1 }} variant="outlined">
          Import CSV
          <input style={{ opacity: "0", zIndex: 1, position: "absolute" }} type="file" accept=".csv" onChange={(e) => importFile(e.target.files[0])} />
        </Button>
      </Grid> */}
    </Grid>
  );
};

export default PropertyTable;
